import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Music2, Users2, SlidersHorizontal, Clock, Mic2,
  Bell, Search, LogOut, Settings, Menu, X, Home, Music, Zap, Radio, PlayCircle,
  AudioLines, CalendarCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoLockup, LogoMark } from "@/components/Logo";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, testId: "nav-item-dashboard" },
  { name: "Services & Roster", path: "/services", icon: CalendarDays, testId: "nav-item-services" },
  { name: "Song Library", path: "/songs", icon: Music2, testId: "nav-item-songs" },
  { name: "Practice Mode", path: "/practice", icon: PlayCircle, testId: "nav-item-practice" },
  { name: "Track Studio", path: "/tracks", icon: AudioLines, testId: "nav-item-tracks" },
  { name: "Pad & Beat Studio", path: "/studio", icon: SlidersHorizontal, testId: "nav-item-studio" },
  { name: "Metronome & Tools", path: "/tools", icon: Clock, testId: "nav-item-tools" },
  { name: "Rehearsal Room", path: "/rehearsal", icon: Mic2, testId: "nav-item-rehearsal" },
  { name: "My Availability", path: "/availability", icon: CalendarCheck, testId: "nav-item-availability" },
  { name: "Team Directory", path: "/team", icon: Users2, testId: "nav-item-team" },
];

const MOBILE = [
  { name: "Home", path: "/dashboard", icon: Home, testId: "mobile-nav-home" },
  { name: "Roster", path: "/services", icon: CalendarDays, testId: "mobile-nav-roster" },
  { name: "Songs", path: "/songs", icon: Music, testId: "mobile-nav-songs" },
  { name: "Practice", path: "/practice", icon: Zap, testId: "mobile-nav-practice" },
  { name: "Pads", path: "/studio", icon: Radio, testId: "mobile-nav-pads" },
];

const ROLE_LABELS = {
  super_admin: "Super Admin", worship_director: "Worship Director", worship_leader: "Worship Leader",
  musician: "Musician", vocalist: "Vocalist", tech_sound: "Tech / Sound", view_only: "View Only",
};

function NavItem({ item, onClick }) {
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      data-testid={item.testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
          isActive
            ? "bg-[#E5C378]/12 text-[#E5C378] border border-[#E5C378]/25"
            : "text-[#B8C1D1] hover:text-[#F7F5F0] hover:bg-white/5 border border-transparent"
        }`
      }
    >
      <item.icon className="w-[18px] h-[18px] shrink-0" />
      <span className="truncate">{item.name}</span>
    </NavLink>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E7D97]" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search songs, people, services…"
        className="w-full bg-[#0B101D] border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-[#F7F5F0] placeholder:text-[#6E7D97] focus:outline-none focus:border-[#E5C378]/40"
      />
      {res && (q.trim()) && (
        <div className="absolute z-50 mt-2 w-full wf-glass border border-white/10 rounded-xl p-2 shadow-2xl max-h-80 overflow-auto">
          {["songs", "people", "services"].map((grp) =>
            res[grp]?.length ? (
              <div key={grp} className="mb-1">
                <div className="eyebrow px-2 py-1">{grp}</div>
                {res[grp].map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setQ("");
                      nav(grp === "songs" ? `/songs/${r.id}` : grp === "people" ? `/team/${r.id}` : `/services/${r.id}`);
                    }}
                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-white/5 text-sm text-[#F7F5F0]"
                  >
                    {r.title || r.name}{" "}
                    <span className="text-[#6E7D97] text-xs">{r.artist || r.role || r.date}</span>
                  </button>
                ))}
              </div>
            ) : null
          )}
          {!res.songs?.length && !res.people?.length && !res.services?.length && (
            <div className="px-2 py-3 text-sm text-[#6E7D97]">No results.</div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const load = async () => { try { const { data } = await api.get("/notifications"); setItems(data); } catch (e) {} };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative p-2 rounded-xl hover:bg-white/5 text-[#B8C1D1]">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#E5C378] text-[#0A0E1A] text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 wf-glass border-white/10 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-display font-semibold text-[#F7F5F0]">Notifications</span>
          <button
            onClick={async () => { await api.post("/notifications/read-all"); load(); }}
            className="text-xs text-[#E5C378] hover:underline" data-testid="notifications-read-all"
          >
            Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 && <div className="px-4 py-6 text-sm text-[#6E7D97]">You're all caught up.</div>}
          {items.map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b border-white/5 ${n.read ? "opacity-60" : ""}`}>
              <div className="text-sm text-[#F7F5F0] font-medium">{n.title}</div>
              <div className="text-xs text-[#8A97AC] mt-0.5">{n.body}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isAdmin = ["super_admin", "worship_director"].includes(user?.role);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const initials = useMemo(
    () => (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase(),
    [user]
  );

  const SidebarInner = ({ onNav }) => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-[#E5C378]/12">
        <LogoLockup />
      </div>
      <nav className="flex-1 overflow-auto px-3 py-4 space-y-1">
        {NAV.map((i) => <NavItem key={i.path} item={i} onClick={onNav} />)}
        {isAdmin && (
          <NavItem item={{ name: "Administration", path: "/admin", icon: Settings, testId: "nav-item-admin" }} onClick={onNav} />
        )}
      </nav>
      <div className="px-3 pb-3">
        <button
          onClick={() => nav("/stage")}
          data-testid="stage-mode-launch"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#080C14] border border-[#E5C378]/30 text-[#E5C378] text-sm font-semibold"
        >
          <span className="w-2 h-2 rounded-full bg-[#E5C378] wf-live-dot" /> STAGE LIVE MODE
        </button>
      </div>
      <div className="px-4 py-3 border-t border-white/10 text-[10px] text-[#6E7D97] italic leading-relaxed">
        "At the right time, I, the LORD, will make it happen." — Isaiah 60:22
      </div>
    </div>
  );

  return (
    <div className="wf-app-bg min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0B101D]/90 border-r border-[#E5C378]/12 backdrop-blur-xl z-40">
        <SidebarInner />
      </aside>

      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-30 wf-glass border-b border-[#E5C378]/12 px-4 sm:px-6 py-3 flex items-center gap-3">
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="p-2 rounded-xl hover:bg-white/5 text-[#B8C1D1]" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[#0B101D] border-r border-[#E5C378]/12">
                <SidebarInner onNav={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
          <div className="hidden sm:block flex-1"><GlobalSearch /></div>
          <div className="flex-1 sm:hidden"><LogoMark size={32} /></div>
          <NotificationBell />
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="profile-menu" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-white/5">
                <div className="w-9 h-9 rounded-full border-2 border-[#E5C378]/40 bg-[#18233C] flex items-center justify-center font-bold text-[#E5C378] text-sm">
                  {initials}
                </div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-medium text-[#F7F5F0]">{user?.name}</div>
                  <div className="text-[10px] text-[#E5C378]">{ROLE_LABELS[user?.role]}</div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 wf-glass border-white/10 p-2">
              <div className="px-2 py-2 border-b border-white/10 mb-1">
                <div className="text-sm text-[#F7F5F0]">{user?.name}</div>
                <div className="text-xs text-[#6E7D97]">{user?.email}</div>
              </div>
              {isAdmin && (
                <button onClick={() => nav("/admin")} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-sm text-[#B8C1D1]">
                  <Settings className="w-4 h-4" /> Administration
                </button>
              )}
              <button onClick={logout} data-testid="logout-button" className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-sm text-[#F87171]">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </PopoverContent>
          </Popover>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-24 lg:pb-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 h-16 wf-glass border-t border-[#E5C378]/20 flex items-center justify-around px-2">
        {MOBILE.map((i) => (
          <NavLink
            key={i.path} to={i.path} data-testid={i.testId}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1 text-[10px] font-medium ${isActive ? "text-[#E5C378]" : "text-[#8A97AC]"}`
            }
          >
            <i.icon className="w-5 h-5" /> {i.name}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
